import { useState, useRef, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { supabase } from '@/integrations/supabase/client';
import { X, Send } from 'lucide-react';
import alexAvatar from '@/assets/alex-avatar.png';
import { useGeoRegion } from '@/hooks/useGeoRegion';

interface Message {
  role: 'a' | 'u';
  text: string;
  isGate?: boolean;
}

type LoginPhase = 'idle' | 'name' | 'email' | 'done';

const RESTRICTED_SYSTEM_PROMPT = `You are Alex, part of the ADD Individual Solutions team in Cyprus. You're friendly, a bit witty, and you talk like a real person — not a chatbot. You ONLY discuss ADD's services, pricing, team, process, and tools. If someone asks about unrelated topics, casually redirect: "That's a bit outside my lane — I'm all about ADD's stuff. But if you want the full AI experience, pop over from Europe or book a call with us!" Always match the language the user writes in. Use contractions, vary sentence length, never open with robotic phrases like "Great question!" or "Certainly!".`;

const AlexChat = () => {
  const { t } = useTranslation();
  const { isEurope } = useGeoRegion();
  const SESSION_LIMIT = isEurope ? 15 : 2;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);
  const [gated, setGated] = useState(false);
  const msgsRef = useRef<HTMLDivElement>(null);

  // Login gate state — starts idle, triggered only on free-form input
  const [loginPhase, setLoginPhase] = useState<LoginPhase>('idle');
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [sessionId] = useState(() => crypto.randomUUID());
  const [emailAttempts, setEmailAttempts] = useState(0);
  const [pendingQuestion, setPendingQuestion] = useState('');

  const alexT = t.alex as any;

  const scrollBottom = () => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  };

  useEffect(scrollBottom, [messages, typing]);

  // VIP IP auto-recognition on mount
  useEffect(() => {
    const checkVip = async () => {
      try {
        const { data } = await supabase.functions.invoke('chat-playground', {
          body: { checkVipIp: true },
        });
        if (data?.vip) {
          setVisitorName(data.name || '');
          setVisitorEmail(data.email || '');
          setLoginPhase('done');
        }
      } catch (_e) { /* ignore */ }
    };
    checkVip();
  }, []);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && messages.length === 0) {
      setTimeout(() => {
        setMessages([{ role: 'a', text: alexT.greeting }]);
      }, 300);
    }
  };

  const showGateMessage = () => {
    const gateText = !isEurope
      ? (alexT.gateRestricted?.message || alexT.gate?.message || "You've reached the session limit. Book a call to continue.")
      : (alexT.gate?.message || "You've reached the session limit. Book a call to continue.");
    setMessages(prev => [...prev, { role: 'a', text: gateText, isGate: true }]);
    setGated(true);
  };

  // After login completes, auto-send the pending question
  useEffect(() => {
    if (loginPhase === 'done' && pendingQuestion) {
      const q = pendingQuestion;
      setPendingQuestion('');
      sendMessageToAI(q);
    }
  }, [loginPhase]);

  const handleLoginInput = async (text: string) => {
    if (!text.trim()) return;

    if (loginPhase === 'name') {
      const name = text.trim();
      setVisitorName(name);
      const userMsg: Message = { role: 'u', text: name };
      const askEmailText = (alexT.askEmail || "Nice to meet you, {name}! Drop me your email so I know you're a real person and not a sneaky bot.").replace('{name}', name);
      setMessages(prev => [...prev, userMsg, { role: 'a', text: askEmailText }]);
      setInput('');
      setLoginPhase('email');
      return;
    }

    if (loginPhase === 'email') {
      const emailInput = text.trim();
      const userMsg: Message = { role: 'u', text: emailInput };
      setMessages(prev => [...prev, userMsg]);
      setInput('');

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailInput)) {
        setMessages(prev => [...prev, { role: 'a', text: alexT.emailInvalid || "That doesn't look like a real email address — mind double-checking?" }]);
        return;
      }

      setTyping(true);
      try {
        const { data, error } = await supabase.functions.invoke('chat-playground', {
          body: {
            validateEmailOnly: true,
            visitorEmail: emailInput,
            visitorName,
            sessionId,
            source: 'alex',
          },
        });

        if (error) throw error;

        if (data?.emailInvalid) {
          setEmailAttempts(prev => prev + 1);
          const reason = data.reason;
          let responseText: string;
          if (reason === 'disposable') {
            responseText = alexT.emailDisposable || "Nice try! But I need a real email, not a throwaway one. Just so I know you're human.";
          } else if (reason === 'suspicious') {
            responseText = alexT.emailSuspicious || "That email looks a bit... creative. I need a real one you actually check — no test addresses or keyboard mashing, please.";
          } else if (emailAttempts >= 1) {
            responseText = alexT.emailRefused || "I get it, privacy matters. But without an email I can't tell you apart from the bots. It's a one-time thing, promise.";
          } else {
            responseText = alexT.emailInvalid || "That doesn't look like a real email address — mind double-checking?";
          }
          setMessages(prev => [...prev, { role: 'a', text: responseText }]);
          return;
        }

        // Email is valid
        setVisitorEmail(emailInput);
        setLoginPhase('done');
        const welcomeText = (alexT.welcomeBack || "Hey {name}! Good to have you. What can I help with?").replace('{name}', visitorName);
        setMessages(prev => [...prev, { role: 'a', text: welcomeText }]);
      } catch {
        setMessages(prev => [...prev, { role: 'a', text: alexT.fallback || "Something went wrong. Try again?" }]);
      } finally {
        setTyping(false);
      }
      return;
    }
  };

  /** Send a message directly to the AI (post-login or chip) */
  const sendMessageToAI = async (text: string, isChip = false) => {
    const userMsg: Message = { role: 'u', text };
    const updated = [...messages, userMsg];
    setMessages(updated);

    if (!isChip) {
      const newCount = sessionCount + 1;
      setSessionCount(newCount);
      if (newCount > SESSION_LIMIT) {
        showGateMessage();
        return;
      }
    }

    setTyping(true);

    try {
      const apiMessages = updated.map(m => ({
        role: m.role === 'a' ? 'ai' : 'user',
        text: m.text,
      }));
      const { data, error } = await supabase.functions.invoke('chat-playground', {
        body: {
          messages: apiMessages,
          source: 'alex',
          visitorName: visitorName || undefined,
          visitorEmail: visitorEmail || undefined,
          sessionId,
          ...((!isEurope) && { systemPromptOverride: RESTRICTED_SYSTEM_PROMPT }),
        },
      });

      if (data?.rateLimited) {
        setMessages(prev => [...prev, { role: 'a', text: data.reply, isGate: true }]);
        setGated(true);
        return;
      }

      if (error || data?.error) throw new Error(data?.error || error?.message);
      setMessages(prev => [...prev, { role: 'a', text: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'a', text: t.alex.fallback }]);
    } finally {
      setTyping(false);
    }
  };

  const sendMessage = async (text: string, isChip = false) => {
    if (!text.trim() || typing || gated) return;

    // Chips bypass login gate entirely
    if (isChip) {
      setInput('');
      await sendMessageToAI(text, true);
      return;
    }

    // Free-form input: if not logged in, trigger gate
    if (loginPhase === 'idle') {
      setPendingQuestion(text.trim());
      setInput('');
      const askNameText = alexT.askNameBeforeAnswer || "Hold on — before I answer that, what's your name?";
      setMessages(prev => [...prev, { role: 'u', text: text.trim() }, { role: 'a', text: askNameText }]);
      setLoginPhase('name');
      return;
    }

    // If in login flow (name/email), handle login input
    if (loginPhase !== 'done') {
      handleLoginInput(text);
      return;
    }

    // Logged in — send normally
    setInput('');
    await sendMessageToAI(text);
  };

  const gate = alexT.gate;

  return (
    <div className="alex-container">
      {/* Toggle */}
      <button className="alex-toggle" onClick={toggleOpen}>
        {open ? <X size={24} /> : <img src={alexAvatar} alt="Alex" width={56} height={56} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />}
        {!open && <span className="alex-tag">{t.alex.tag}</span>}
      </button>

      {/* Window */}
      <div className={`alex-window${open ? ' open' : ''}`}>
        <div className="alex-head">
          <div className="alex-av"><img src={alexAvatar} alt="Alex" width={36} height={36} decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /></div>
          <div className="alex-head-info">
            <h4>{t.alex.name}</h4>
            <p>{t.alex.status}</p>
          </div>
          <button className="alex-x" onClick={() => setOpen(false)}>
            <X size={14} />
          </button>
        </div>

        <div className="alex-msgs" ref={msgsRef}>
          {messages.map((msg, i) => (
            <div className={`am ${msg.role}${msg.isGate ? ' gate' : ''}`} key={i}>
              <div className="am-av">{msg.role === 'a' ? <img src={alexAvatar} alt="Alex" width={28} height={28} decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : '?'}</div>
              <div className="am-b">{msg.text}</div>
            </div>
          ))}
          {typing && (
            <div className="am a">
              <div className="am-av"><img src={alexAvatar} alt="Alex" width={28} height={28} decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /></div>
              <div className="am-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
          {gated && (
            <div className="alex-gate-cta">
              <a href="/#contact" className="alex-gate-btn primary">{gate?.cta1 || 'Book a Call'}</a>
              <a href="mailto:contact@add-individual-solutions.com" className="alex-gate-btn secondary">{gate?.cta2 || 'Email Us'}</a>
            </div>
          )}
        </div>

        {/* Chips always visible (unless gated) */}
        {!gated && (
          <div className="alex-chips">
            {t.alex.chips.map((chip, i) => (
              <button className="a-chip" key={i} onClick={() => sendMessage(chip, true)}>
                {chip}
              </button>
            ))}
          </div>
        )}

        <div className="alex-inp">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage(input)}
            placeholder={
              gated
                ? (gate?.cta1 || 'Session limit reached')
                : loginPhase === 'name'
                ? (alexT.namePlaceholder || 'Your name...')
                : loginPhase === 'email'
                ? (alexT.emailPlaceholder || 'Your email...')
                : t.alex.placeholder
            }
            disabled={gated}
          />
          <button onClick={() => sendMessage(input)} disabled={gated}>
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlexChat;
